import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import { Button, GetInTouch, LogoRedLine, SearchDropdownMenu } from 'components';
import { LocalContext } from 'context';
import { useWindowSize } from 'hooks';
import { Link, navigate } from 'gatsby';
import slugify from 'slugify';
import {
  Logo as SmallLogo,
  LongLogo,
  SearchIcon,
  CartIcon,
  DownArrow,
  FbIcon,
  IgIcon,
  LinkedinIcon,
  TwitterIcon,
  WhatsappIcon
} from 'assets/svgs/';

function Indicator({ open }) {
  return (
    <AnimatePresence>
      {open ? (
        <Ellipse initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} />
      ) : (
        <motion.div initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }}>
          <DownArrow />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function MobileAndTabletHeader({ stairCollections, doorCollections }) {
  const { cartCounter, counterRef, cartCounterDummyRef, showMobileMenu, setShowMobileMenu } =
    useContext(LocalContext);
  const { windowWidth } = useWindowSize();
  const [activeLink, setActiveLink] = useState(null);
  const [showSearchDropdownMenu, setShowSearchDropdownMenu] = useState(false);

  const handleClickMenuItem = (value) => {
    if (activeLink === value) {
      setActiveLink(null);
    } else {
      setActiveLink(value);
    }
  };

  const handleClickLink = () => setShowMobileMenu(false);

  const toggleDropdownMenu = (e) => {
    e.preventDefault();
    setShowMobileMenu((prevState) => !prevState);
  };

  return (
    <Container $showMobileMenu={showMobileMenu}>
      <div>
        <StyledGetInTouch />
        <Link to="/">
          {windowWidth < 650 ? (
            <SmallLogo />
          ) : (
            <>
              <StyledLongLogo windowWidth={windowWidth} />
              <LogoRedLine />
            </>
          )}
        </Link>
        <StyledButton onClick={toggleDropdownMenu} color={showMobileMenu ? 'secondary' : 'primary'}>
          <motion.div animate={{ rotateZ: showMobileMenu ? 45 : 0, y: showMobileMenu ? 2 : 0 }} />
          {!showMobileMenu && (
            <>
              <div />
              <div />
            </>
          )}
          <motion.div animate={{ rotateZ: showMobileMenu ? -45 : 0, y: showMobileMenu ? -3 : 0 }} />
        </StyledButton>
        <Icons>
          <Cart to="/request-a-quote/">
            <CartIcon />
            <CartCounterDummy ref={cartCounterDummyRef} />
            <CartCounter>
              <span ref={counterRef}>{cartCounter > 99 ? <small>99+</small> : cartCounter}</span>
            </CartCounter>
          </Cart>
          <StyledSearchIcon
            onClick={() => setShowSearchDropdownMenu((currentValue) => !currentValue)}
          />
        </Icons>
        <AnimatePresence>
          {showSearchDropdownMenu && (
            <SearchDropdownMenu
              windowWidth={windowWidth}
              setShowSearchDropdownMenu={setShowSearchDropdownMenu}
            />
          )}
        </AnimatePresence>
      </div>
      <AnimatePresence>
        {showMobileMenu && (
          <DropdownMenu>
            <MenuList>
              <MenuItem
                onClick={() => handleClickMenuItem('staircases')}
                active={activeLink === 'staircases'}>
                Staircases <Indicator open={activeLink === 'staircases'} />
              </MenuItem>
              <AnimatePresence>
                {activeLink === 'staircases' && (
                  <FurtherDropdownList>
                    {stairCollections.nodes.map(({ frontmatter }) => {
                      const slug = slugify(frontmatter.slug, {
                        remove: /[*+~.,()'"!:@/]/g,
                        lower: true,
                        trim: true
                      });
                      return (
                        <li key={slug}>
                          <Link to={`/stairs/${slug}`} onClick={handleClickLink}>
                            {frontmatter.title.split(' ')[0]}
                          </Link>
                        </li>
                      );
                    })}
                    <li style={{ marginTop: '1.65rem', fontWeight: 'bold' }}>
                      <Link to="/stairs/" onClick={handleClickLink}>
                        View All Stairs
                      </Link>
                    </li>
                  </FurtherDropdownList>
                )}
              </AnimatePresence>
              <MenuItem
                onClick={() => handleClickMenuItem('doors')}
                active={activeLink === 'doors'}>
                Doors <Indicator open={activeLink === 'doors'} />
              </MenuItem>
              <AnimatePresence>
                {activeLink === 'doors' && (
                  <FurtherDropdownList>
                    {doorCollections.nodes.map(({ frontmatter }) => {
                      const slug = slugify(frontmatter.title, {
                        remove: /[*+~.,()'"!:@/]/g,
                        lower: true,
                        trim: true
                      });
                      return (
                        <li key={slug}>
                          <Link to={`/doors/${slug}`} onClick={handleClickLink}>
                            {frontmatter.title === 'Masonite Hollow Core'
                              ? 'Masonite HC'
                              : frontmatter.title}
                          </Link>
                        </li>
                      );
                    })}
                    <li style={{ marginTop: '1.65rem', fontWeight: 'bold' }}>
                      <Link to="/doors/" onClick={handleClickLink}>
                        View All Doors
                      </Link>
                    </li>
                  </FurtherDropdownList>
                )}
              </AnimatePresence>
              <MenuItem
                onClick={() => handleClickMenuItem('stair-and-door-parts')}
                active={activeLink === 'stair-and-door-parts'}>
                Stair & Door Parts <Indicator open={activeLink === 'stair-and-door-parts'} />
              </MenuItem>
              <AnimatePresence>
                {activeLink === 'stair-and-door-parts' && (
                  <FurtherDropdownList>
                    <li>
                      <Link
                        to="/stair-and-door-parts/?collections=bannisters"
                        onClick={handleClickLink}>
                        Bannisters
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/stair-and-door-parts/?collections=brackets,baserails,caps,clamps,posts,handrails,bases,tops,risers,spindles,slips,strings,treads"
                        onClick={handleClickLink}>
                        Stairparts
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/stair-and-door-parts/?collections=door frames,sliding doors,fire seals,architraves,skirtings,decorative,mouldings,corner blocks,oak,ogee"
                        onClick={handleClickLink}>
                        Door Accessories
                      </Link>
                    </li>
                    {/* <li>
                      <Link to="/stair-and-door-parts/" onClick={handleClickLink}>
                        Other
                      </Link>
                    </li> */}
                    <li style={{ marginTop: '1.65rem', fontWeight: 'bold' }}>
                      <Link to="/stair-and-door-parts/" onClick={handleClickLink}>
                        View All Parts
                      </Link>
                    </li>
                  </FurtherDropdownList>
                )}
              </AnimatePresence>
              <MenuItem
                onClick={() => handleClickMenuItem('showcases')}
                active={activeLink === 'showcases'}>
                Showcases <Indicator open={activeLink === 'showcases'} />
              </MenuItem>
              <AnimatePresence>
                {activeLink === 'showcases' && (
                  <FurtherDropdownList>
                    <li>
                      <Link
                        to="/showcases/"
                        style={{ fontWeight: 'bold' }}
                        onClick={handleClickLink}>
                        View All Showcases
                      </Link>
                    </li>
                  </FurtherDropdownList>
                )}
              </AnimatePresence>
              <MenuItem
                onClick={() => handleClickMenuItem('about-us')}
                active={activeLink === 'about-us'}>
                About Us <Indicator open={activeLink === 'about-us'} />
              </MenuItem>
              <AnimatePresence>
                {activeLink === 'about-us' && (
                  <FurtherDropdownList>
                    <li>
                      <Link
                        to="/about-us/"
                        style={{ fontWeight: 'bold' }}
                        onClick={handleClickLink}>
                        About Us
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/guides-and-manuals/"
                        style={{ fontWeight: 'bold' }}
                        onClick={handleClickLink}>
                        Guides & Manuals
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/trade-partners/"
                        style={{ fontWeight: 'bold' }}
                        onClick={handleClickLink}>
                        Trade Partners
                      </Link>
                    </li>
                  </FurtherDropdownList>
                )}
              </AnimatePresence>
              <MenuItem
                onClick={() => {
                  navigate('/trade-partners/');
                  handleClickMenuItem('trade');
                  handleClickLink();
                }}
                active={activeLink === 'trade'}>
                Trade
              </MenuItem>
            </MenuList>
            <Footer>
              <span>
                Contact us with your request
                <br />
                and we&apos;ll get back to you promptly
              </span>
              <SocialIcons>
                <FbIcon />
                {/* <TwitterIcon /> */}
                <IgIcon />
                <LinkedinIcon />
                {/* <WhatsappIcon /> */}
              </SocialIcons>
            </Footer>
          </DropdownMenu>
        )}
      </AnimatePresence>
    </Container>
  );
}

const StyledGetInTouch = styled(GetInTouch)`
  grid-column: 1/10;

  @media (width >= 450px) {
    grid-column: 1/-1;
  }
`;

const Container = styled.header`
  background-color: rgb(245 245 245 / 100%);
  display: grid;
  grid-template-rows: auto 1fr;
  height: ${({ $showMobileMenu }) => ($showMobileMenu ? 'auto' : '110px')};
  min-height: ${({ $showMobileMenu }) => ($showMobileMenu ? '100vh' : 'unset')};
  position: sticky;
  top: 0;
  width: 100vw;
  z-index: 3;

  @media (width >= 650px) {
    height: 107px;
  }

  @media (width >= 1200px) {
    display: none;
  }

  > div {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    grid-template-rows: auto auto;
    justify-items: center;
    padding: 0 0.625rem;
    position: relative;
    row-gap: 20px;

    /* Logo */
    > a {
      align-self: end;
      grid-column: 5/9;
      grid-row: 2/3;
      height: 58px;
      width: 228px;

      @media (width >= 650px) {
        grid-column: 2/12;
        height: initial;
        width: 100%;
        margin-bottom: 9px;
      }

      @media (width >= 900px) {
        grid-column: 1/-1;
      }
    }
  }
`;

const StyledButton = styled(Button)`
  border-radius: 0 0 6px 6px !important;
  display: flex;
  flex-direction: column;
  gap: 3px;
  height: 34px;
  min-width: 40px !important;
  padding: 8px 10px !important;
  position: absolute !important;
  right: 0;
  top: 0;
  width: 40px;

  div {
    background: #fff;
    height: 2px;
    width: 20px;
  }
`;

const StyledLongLogo = styled(LongLogo)`
  display: block;
  margin: 0 auto;
  max-width: 728px;
  width: 100%;
`;

const Icons = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  grid-column: 11/13;
  grid-row: 2/3;
  margin-bottom: 4px;
  place-self: center end;

  > svg * {
    stroke: var(--connolly-black);
  }
`;

const StyledSearchIcon = styled(SearchIcon)`
  cursor: pointer;
  position: relative;
  right: 0.1625rem;
  width: 1.25rem;
`;

const Cart = styled(Link)`
  position: relative;
  width: 1rem;
`;

const CartCounterDummy = styled.div`
  aspect-ratio: 1;
  backface-visibility: hidden;
  border-radius: 100%;
  bottom: -0.5625rem;
  height: 1.25rem;
  left: -0.9375rem;
  position: absolute;
  width: 1.25rem;

  > * {
    position: relative;
  }
`;

const CartCounter = styled.strong`
  align-items: center;
  aspect-ratio: 1;
  background-color: var(--connolly-red);
  border-radius: 100%;
  bottom: -0.5625rem;
  display: flex;
  font-size: 0.75rem;
  justify-content: center;
  left: -0.9375rem;
  overflow: hidden;
  position: absolute;
  text-align: center;
  z-index: 1;

  span {
    color: #fff;
    width: 3ch;
  }
`;

const DropdownMenu = styled(motion.div).attrs({
  initial: {
    height: 0,
    opacity: 0
  },
  animate: {
    height: '100%',
    opacity: 1
  },
  exit: {
    height: '100%',
    opacity: 0
  },
  transition: {
    type: 'tween',
    ease: 'easeInOut',
    duration: 0.5
  }
})`
  background-color: #f5f5f5;
  column-gap: 1.25rem;
  padding: 0 !important;
  display: flex !important;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  z-index: 4;
`;

const MenuList = styled.ul`
  display: flex;
  flex-direction: column;
  padding: 0 0.625rem;
  width: 100%;

  @media (width >= 768px) {
    max-width: 36.25rem;
    margin: 0 auto;
  }
`;

const MenuItem = styled.li`
  align-items: center;
  color: ${({ active }) => (active ? 'var(--connolly-red)' : 'var(--connolly-black)')};
  display: flex;
  font-size: 1.5rem;
  font-weight: ${({ active }) => (active ? '700' : '400')};
  justify-content: space-between;
  padding: 1.5rem 0;
  position: relative;
  transition: all 0.2s ease-in-out;

  svg {
    height: 14px;
    width: 21px;

    path {
      fill: var(--connolly-red);
    }
  }

  &::before {
    background-color: var(--connolly-red);
    bottom: 0;
    content: '';
    height: 3px;
    left: 0;
    position: absolute;
    transition: all 0.5s ease-in-out;
    width: ${({ active }) => (active ? '0' : '100%')};
  }
`;

const FurtherDropdownList = styled(motion.ul).attrs({
  initial: {
    opacity: 0,
    height: 0
  },
  animate: {
    opacity: 1,
    height: '100%',
    transition: {
      opacity: {
        delay: 0.2
      },
      delay: 0.1
    }
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: {
      height: {
        delay: 0.2
      },
      delay: 0
    }
  }
})`
  display: flex;
  flex-direction: column;
  padding: 0.625rem 0.625rem 0;

  li {
    padding: 0.5rem 0;
  }

  a {
    color: var(--connolly-black);
    display: inline-block;
    font-size: 1.25rem;
    height: 100%;
    text-decoration: none;
    width: 100%;
  }
`;

const Footer = styled.section`
  background: rgb(149 164 177 / 46%);
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
  display: flex;
  flex-direction: column;
  font-size: 1rem;
  font-weight: 700;
  gap: 1rem;
  margin: 0 auto;
  margin-top: 1.25rem;
  max-width: 358px;
  padding: 0.625rem 0.85rem;
  text-align: center;
  text-transform: uppercase;

  @media (width >= 768px) {
    margin: 0 auto;
  }
`;

const SocialIcons = styled.div`
  column-gap: 1.75rem;
  display: grid;
  grid-template-columns: repeat(3, 1.5rem);
  margin: 0 auto;

  svg {
    height: 1.5rem;
    width: 1.5rem;
  }
`;

const Ellipse = styled(motion.div)`
  background-color: var(--connolly-red);
  border-radius: 50%;
  height: 8px;
  margin-right: 0.5rem;
  width: 8px;
`;

export default MobileAndTabletHeader;
